/**
 * المفضلة (Wishlist) — هلا ومسهلا
 *
 * Dependency-free wishlist stored in the visitor's browser (localStorage).
 * Works for guests and logged-in customers alike; it is per browser/device.
 *
 * Storage shape (key "hm:wishlist"): [{ "id": "123", "handle": "product-handle" }]
 * The handle is stored so the wishlist page can load the product later.
 *
 * Custom elements defined here:
 *   <product-favorite-button data-product-id data-product-handle>  heart toggle
 *
 * Other modules can import { wishlist, WISHLIST_CHANGE_EVENT } from this file.
 */

const STORAGE_KEY = 'hm:wishlist';
export const WISHLIST_CHANGE_EVENT = 'wishlist:change';

/**
 * @typedef {{ id: string, handle: string }} WishlistItem
 */

/**
 * @param {unknown} value
 * @returns {WishlistItem[]}
 */
function normalize(value) {
  if (!Array.isArray(value)) return [];

  const seen = new Set();
  /** @type {WishlistItem[]} */
  const items = [];

  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const id = String(/** @type {any} */ (entry).id ?? '');
    const handle = String(/** @type {any} */ (entry).handle ?? '');
    if (!id || !handle || seen.has(id)) continue;
    seen.add(id);
    items.push({ id, handle });
  }

  return items;
}

/** @returns {WishlistItem[]} */
function read() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? normalize(JSON.parse(raw)) : [];
  } catch (error) {
    return [];
  }
}

/**
 * @param {WishlistItem[]} items
 * @param {{ productId?: string, favorited?: boolean }} [detail]
 */
function write(items, detail = {}) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    // Storage may be unavailable (private mode / quota). Fail quietly so the
    // rest of the page keeps working.
  }
  document.dispatchEvent(new CustomEvent(WISHLIST_CHANGE_EVENT, { detail: { items, ...detail } }));
}

export const wishlist = {
  read,

  /** @returns {number} */
  count() {
    return read().length;
  },

  /**
   * @param {string | number} id
   * @returns {boolean}
   */
  has(id) {
    const key = String(id);
    return read().some((item) => item.id === key);
  },

  /** @param {{ id: string | number, handle: string }} item */
  add(item) {
    const id = String(item.id);
    const items = read();
    if (items.some((entry) => entry.id === id)) return;
    items.push({ id, handle: String(item.handle) });
    write(items, { productId: id, favorited: true });
  },

  /** @param {string | number} id */
  remove(id) {
    const key = String(id);
    const items = read();
    const next = items.filter((entry) => entry.id !== key);
    if (next.length === items.length) return;
    write(next, { productId: key, favorited: false });
  },

  /**
   * @param {{ id: string | number, handle: string }} item
   * @returns {boolean} true when the product is now in the wishlist
   */
  toggle(item) {
    if (this.has(item.id)) {
      this.remove(item.id);
      return false;
    }
    this.add(item);
    return true;
  },

  clear() {
    write([], { favorited: false });
  },
};

class ProductFavoriteButton extends HTMLElement {
  connectedCallback() {
    this.productId = String(this.getAttribute('data-product-id') || '');
    this.productHandle = String(this.getAttribute('data-product-handle') || '');
    this.button = this.querySelector('button');

    if (!this.productId || !this.button) return;

    this.addLabel = this.getAttribute('data-add-label') || 'إضافة إلى المفضلة';
    this.removeLabel = this.getAttribute('data-remove-label') || 'إزالة من المفضلة';

    this.button.addEventListener('click', this.#handleClick);
    document.addEventListener(WISHLIST_CHANGE_EVENT, this.#sync);
    window.addEventListener('pageshow', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
    document.removeEventListener(WISHLIST_CHANGE_EVENT, this.#sync);
    window.removeEventListener('pageshow', this.#sync);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    // The heart sits on top of product cards: it must never open the product
    // page or trigger a nearby add-to-cart action.
    event.preventDefault();
    event.stopPropagation();

    if (!this.productHandle) return;
    wishlist.toggle({ id: this.productId, handle: this.productHandle });
  };

  #sync = () => {
    if (!this.button) return;
    const favorited = wishlist.has(this.productId);

    this.classList.toggle('is-favorited', favorited);
    this.button.setAttribute('aria-pressed', favorited ? 'true' : 'false');
    this.button.setAttribute('aria-label', favorited ? this.removeLabel : this.addLabel);
  };
}

if (!customElements.get('product-favorite-button')) {
  customElements.define('product-favorite-button', ProductFavoriteButton);
}

// Keep other tabs in sync.
window.addEventListener('storage', (event) => {
  if (event.key && event.key !== STORAGE_KEY) return;
  document.dispatchEvent(new CustomEvent(WISHLIST_CHANGE_EVENT, { detail: { items: read() } }));
});
